// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Material from '../renderer/Material';
import RenderTarget from '../renderer/pass/RenderTarget';
import Texture from '../renderer/Texture';
import Shader from '../renderer/Shader';
import RenderPass from '../renderer/pass/RenderPass';
import FullscreenPass from '../renderer/pass/FullscreenPass';
import ShaderLibExtra from '../passpack/ShaderLibExtra';
import Pass from '../renderer/pass/Pass';
import Vector3 from '../math/Vector3';
import MathUtils from '../math/MathUtils';
import Capabilities from '../renderer/Capabilities';

/**
 * Screen Space Ambient Occlusion pass
 */
class SsaoPass extends Pass {
  /**
   * @param renderList
   * @param {Object} options
   * @param {Boolean} options.variant determines if using variant ssao or default one variant uses an extra pass for normal
   *   generation.
   * @param {number} options.sampleSize the size of AO sampling. Default is 32
   * @param {downsampleAmount} options.downsampleAmount the down sample size. Default is 2
   */
  constructor(renderList, options) {
    super();
    options = options || {};
    this.variant = options.variant || false;
    this.sampleSize = options.sampleSize || 32;
    this.downsampleAmount = options.downsampleAmount || 2;

    //crete ssao pass
    this._ssaoPass = new FullscreenPass(ShaderLibExtra.ssao);
    this._ssaoPass.useReadBuffer = false;
    //crete post ssao pass
    this._postSsaoPass = new FullscreenPass(ShaderLibExtra.postSsao);

    if (this.variant) {
      //create pass to save normals amd position
      this._normalDepthPass = new RenderPass(renderList);
      const normalDepthMaterial = new Material(ShaderLibExtra.ssaoPassNormalPos);
      normalDepthMaterial.fullOverride = true;
      this._normalDepthPass.overrideMaterial = normalDepthMaterial;

      this._ssaoPass.material.shader.setDefine('VARIANT', true);
    } else {
      //create pass to save depth
      this._normalDepthPass =  new RenderPass(renderList);
      const normalDepthMaterial = new Material(ShaderLibExtra.ssaoPassNormalDepth);
      normalDepthMaterial.fullOverride = true;
      this._normalDepthPass.overrideMaterial = normalDepthMaterial;

      this._ssaoPass.material.shader.removeDefine('VARIANT');
    }

    //create ssao kernel
    this._ssaoPass.material.uniforms.samples = this._createSampleData(this.sampleSize);
    this._ssaoPass.material.shader.setDefine('SAMPLE_SIZE', this.sampleSize);
    // tile noise texture over screen based on screen dimensions divided by noise size
    this._ssaoPass.material.setTexture(Shader.BASE_COLOR_MAP, this._createNoiseTexture(16));

    const width = window.innerWidth || 1024;
    const height = window.innerHeight || 1024;

    this.updateSize({
      x: 0, y: 0, width, height,
    });

    this.enabled = true;
    this.clear = false;
    this.needsSwap = true;
  }

  _createSampleData(sampleSize) {
    const ssaoKernel = new Array(sampleSize * 3);
    for (let i = 0; i < sampleSize; i++) {
      const sampleVec = new Vector3(Math.random() * 2.0 - 1.0, Math.random() * 2.0 - 1.0, Math.random());
      sampleVec.normalize().scale(Math.random());
      let scale = i / sampleSize;
      scale = MathUtils.lerp(0.1, 1.0, scale * scale);
      sampleVec.scale(scale);

      ssaoKernel[i * 3] = ( sampleVec.x );
      ssaoKernel[i * 3 + 1] = ( sampleVec.y );
      ssaoKernel[i * 3 + 2] = ( sampleVec.z );
    }
    return ssaoKernel;
  }

  _createNoiseTexture(sampleSize) {
    const noiseData = new Float32Array(sampleSize * 3);
    for (let i = 0; i < sampleSize * 3; i++) {
      if (i % 3 !== 0)
        noiseData[i] = Math.random() * 2.0 - 1.0;
      else
        noiseData[i] = 0; //z coordinte = 0 as we are rotating around view
    }
    const size = Math.sqrt(sampleSize);

    return new Texture(noiseData, {
      format: Capabilities.Webgl2 ? 'RGB32F' : 'RGB',
      magFilter: 'NearestNeighbor',
      minFilter: 'NearestNeighborNoMipMaps',
      wrapS: 'Repeat',
      wrapR: 'Repeat',
      type: 'Float',
      generateMipmaps: false,
    }, size, size);
  }

  updateSize(size, renderer) {
    const width = Math.floor(size.width / this.downsampleAmount);
    const height = Math.floor(size.height / this.downsampleAmount);

    this._ssaoPass.material.uniforms.size = [size.width, size.height];
    this._postSsaoPass.material.uniforms.size = [size.width, size.height];
    this.destroy(renderer);

    if (this.variant) {
      this._positionTexture = new RenderTarget(width, height, {
        magFilter: 'NearestNeighbor',
        minFilter: 'NearestNeighborNoMipMaps',
        stencilBuffer: false,
        format: Capabilities.Webgl2 ? 'RGBA32F' : 'RGBA',
        type: 'Float',
        generateMipmaps: false

      });

      this._normalTexture = new RenderTarget(width, height, {
        magFilter: 'NearestNeighbor',
        minFilter: 'NearestNeighborNoMipMaps',
        stencilBuffer: false,
        format: Capabilities.Webgl2 ? 'RGBA32F' : 'RGBA',
        type: 'Float',
        generateMipmaps: false
      });
    } else {
      this._normalDepthTexture = new RenderTarget(width, height, {
        magFilter: 'NearestNeighbor',
        minFilter: 'NearestNeighborNoMipMaps',
        stencilBuffer: false,
        generateMipmaps: false,
        format: Capabilities.Webgl2 ? 'RGBA32F' : 'RGBA',
        type: 'Float',
      });
    }

    this._aoBuffer = new RenderTarget(width, height, {
      magFilter: 'NearestNeighbor',
      minFilter: 'NearestNeighborNoMipMaps',
      stencilBuffer: false,
      depthBuffer: false,
      format: Capabilities.Webgl2 ? 'RGBA32F' : 'RGBA',
      type: 'Float',
      generateMipmaps: false
    });
  }

  setRadius(radius) {
    this._ssaoPass.material.shader.uniforms.radius = radius;
  }

  destroy(renderer) {
    if (this._positionTexture) {
      this._positionTexture.destroy(renderer.context);
    }
    if (this._normalTexture) {
      this._normalTexture.destroy(renderer.context);
    }
    if (this._aoBuffer) {
      this._aoBuffer.destroy(renderer.context);
    }
    if (this._normalDepthTexture) {
      this._normalDepthTexture.destroy(renderer.context);
    }
  }

  render(renderer, writeBuffer, readBuffer, delta, maskActive, camera, lights, clearColor) {
    if (this.variant) {
      //save normals into a buffer if using variant pass
      this._normalDepthPass.render(renderer, null, [this._positionTexture, this._normalTexture], delta);
      this._ssaoPass.material.setTexture(Shader.DIFFUSE_MAP, this._normalTexture);
      this._ssaoPass.material.setTexture(Shader.DEPTH_MAP, this._positionTexture);
    } else {
      this._normalDepthPass.render(renderer, null, this._normalDepthTexture, delta);
      this._ssaoPass.material.setTexture(Shader.DIFFUSE_MAP, this._normalDepthTexture);
    }

    this._ssaoPass.render(renderer, this._aoBuffer, readBuffer, delta);
    renderer.setClearColor(clearColor[0], clearColor[1], clearColor[2], clearColor[3]);
    this._postSsaoPass.material.setTexture(Shader.AO_MAP, this._aoBuffer);
    this._postSsaoPass.render(renderer, writeBuffer, readBuffer, delta);
  }
}

export default SsaoPass;
